/**
 * 3D Room Visualizer — Camera Controller
 *
 * Manages spherical orbit camera with drag rotation, zoom, and reset animation.
 */

import * as THREE from 'three';

export interface CameraState {
  rotation: { x: number; y: number };
  distance: number;
  fov: number;
}

// Straight-on view showing 3 walls, floor, and ceiling
export const INITIAL_ROTATION = { x: 0.0, y: 4.0 };
export const DEFAULT_FOV = 50;

/**
 * Compute camera world position from spherical orbit coordinates.
 */
export function computeCameraPosition(
  rotation: { x: number; y: number },
  distance: number,
  center: THREE.Vector3
): THREE.Vector3 {
  const x = distance * Math.sin(rotation.y) * Math.cos(rotation.x);
  const y = distance * Math.sin(rotation.x);
  const z = distance * Math.cos(rotation.y) * Math.cos(rotation.x);

  return new THREE.Vector3(
    x + center.x,
    y + center.y,
    z + center.z
  );
}

/**
 * Apply orbit rotation from mouse drag delta.
 * Returns the new clamped rotation values.
 */
export function applyDragRotation(
  currentRotation: { x: number; y: number },
  deltaX: number,
  deltaY: number,
  sensitivity: number = 0.01
): { x: number; y: number } {
  const newY = currentRotation.y + deltaX * sensitivity;
  const newX = currentRotation.x + deltaY * sensitivity;

  // Clamp vertical rotation to avoid flipping
  const clampedX = Math.max(-Math.PI / 2 + 0.1, Math.min(Math.PI / 2 - 0.1, newX));

  return { x: clampedX, y: newY };
}

/**
 * Apply zoom by adjusting FOV.
 * Returns the new clamped FOV value.
 */
export function applyZoom(currentFov: number, delta: number, speed: number = 2): number {
  const newFov = currentFov + (delta > 0 ? speed : -speed);
  return Math.max(20, Math.min(80, newFov));
}

/**
 * Ease-in-out cubic function for smooth animations.
 */
export function easeInOutCubic(t: number): number {
  return t < 0.5
    ? 2 * t * t
    : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

/**
 * Interpolate between two rotation states for reset animation.
 */
export function interpolateState(
  start: { rotation: { x: number; y: number }; fov: number },
  target: { rotation: { x: number; y: number }; fov: number },
  progress: number
): { rotation: { x: number; y: number }; fov: number } {
  const eased = easeInOutCubic(progress);

  return {
    rotation: {
      x: start.rotation.x + (target.rotation.x - start.rotation.x) * eased,
      y: start.rotation.y + (target.rotation.y - start.rotation.y) * eased,
    },
    fov: start.fov + (target.fov - start.fov) * eased,
  };
}

/**
 * Calculate optimal orbit distance so the room fits the viewport nicely.
 * Uses the bounding sphere of the room geometry for reliable sizing.
 */
export function calculateZoomToFit(
  camera: THREE.PerspectiveCamera,
  roomGroup: THREE.Group,
  _rotation: { x: number; y: number },
  _center: THREE.Vector3
): number {
  // Get bounding sphere of board/surface meshes ONLY
  // Exclude: Sprites (labels), Lines (dimension lines), LineSegments (seam lines)
  const box = new THREE.Box3();
  roomGroup.traverse((child) => {
    if (child instanceof THREE.Mesh
        && !(child instanceof THREE.Sprite)
        && !(child instanceof THREE.Line)) {
      const childBox = new THREE.Box3().setFromObject(child);
      if (!childBox.isEmpty()) box.union(childBox);
    }
  });

  if (box.isEmpty()) {
    return 30;
  }

  const sphere = new THREE.Sphere();
  box.getBoundingSphere(sphere);
  const radius = sphere.radius;

  const fovRad = camera.fov * (Math.PI / 180);
  const vDist = radius / Math.sin(fovRad / 2);
  const hFov = 2 * Math.atan(Math.tan(fovRad / 2) * camera.aspect);
  const hDist = radius / Math.sin(hFov / 2);

  return Math.max(vDist, hDist) * 0.95;
}

/**
 * Update camera position and render the scene.
 */
export function updateCamera(
  camera: THREE.PerspectiveCamera,
  rotation: { x: number; y: number },
  distance: number,
  center: THREE.Vector3,
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene
): void {
  const pos = computeCameraPosition(rotation, distance, center);
  camera.position.copy(pos);
  camera.lookAt(center);
  camera.updateProjectionMatrix();
  renderer.render(scene, camera);
}
